import { Trans, useTranslation } from "react-i18next";
import { useRouterStore } from "@/store/zustandStore";
import { WELCOME_VISITORS_URL } from "@/lib";
import i18n from "@/i18n/config";
import { createFileRoute } from "@tanstack/react-router";
import { useCompanyAdminAccess } from "@/hooks/useCompanyAdminAccess";
import { useWelcomeVisitAccess } from "@/hooks/useWelcomeVisitAccess";
import ReceptionView from "@/components/ReceptionView";
import {
  companyVisitorsOptions,
  companyPreregistrationOptions,
  useCompany,
} from "@/api";
import { useBuildingQuery } from "@/components/RouterSetup";

export const Route = createFileRoute("/_portal/company/$companyId/visitors/")({
  component: CompanyVisitors,
  loader: ({ params }) => {
    const { queryClient } = useRouterStore.getState();
    const { companyId } = params;
    if (!queryClient) return;
    queryClient.ensureQueryData(companyVisitorsOptions(companyId));
    queryClient.ensureQueryData(companyPreregistrationOptions(companyId));
  },
});

function CompanyVisitors() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { allowCompanyAdminAccess } = useCompanyAdminAccess(companyId);
  const { data: company } = useCompany(companyId ?? "");
  const { isLoading: isLoadingBuildingVisitors, data: visitors } =
    useBuildingQuery({
      ...companyVisitorsOptions(companyId ?? ""),
      refetchInterval: 2500,
    });
  const { isLoading: isLoadingPreregistrations, data: preregistrationGroups } =
    useBuildingQuery(companyPreregistrationOptions(companyId ?? ""));

  useWelcomeVisitAccess();
  if (!allowCompanyAdminAccess) {
    return null;
  }
  return (
    <>
      <div className="pageHeader">
        <h1>{t("PAGES.COMPANY_VISITORS.HEADER")}</h1>
        <p>
          <Trans i18nKey="PAGES.COMPANY_VISITORS.SUBHEADER">
            Text
            <a href={WELCOME_VISITORS_URL(i18n.language)} target="_blank"></a>.
          </Trans>
        </p>
      </div>
      <ReceptionView
        navigationPaths={{
          future: `/company/${company.buildingTenantId}/visitors/preregistrations`,
          new: `/company/${company.buildingTenantId}/visitors/preregistrations/new`,
        }}
        companyReception
        isLoadingVisitors={isLoadingBuildingVisitors}
        isLoadingPreregistrations={isLoadingPreregistrations}
        visitors={visitors}
        preregistrationGroups={preregistrationGroups ?? []}
        companyIntegratedWithAccessControlSystem={
          company?.accessControlSystemIntegration
        }
        companyDisableAccessCodeGeneration={
          company?.acsDisableAccessCodeGeneration
        }
      />
    </>
  );
}
